import type React from 'react';
import { useField } from 'formik';
import type { LucideIcon } from 'lucide-react';
import { Textarea } from '@/components/textarea';
import { getFormFieldClasses } from '@/utility/utility';
import { FieldWrapper, type FieldWrapperConfig } from '@/form/FieldWrapper';
import { Input } from '@/components';

/**
 * Props for the FormField component
 */
interface FormFieldProps
  extends
    FieldWrapperConfig,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'> {
  IconSuffix?: LucideIcon | null;
  IconPrefix?: LucideIcon | null;
  className?: string;
  placeholder?: string;
  type?: string;
  onIconClick?: () => void;
  selectOnFocus?: boolean;
}

/**
 * A reusable form field component that wraps an Input or Textarea component,
 * along with error handling and a label.
 */
export const FormField: React.FC<FormFieldProps> = ({
  placeholder,
  type = 'text',
  className = '',
  IconSuffix,
  IconPrefix,
  onIconClick,

  // wrapper props
  ref,
  name,
  label,
  infoText,
  disabled = false,
  fieldClassName,
  showErrorSpace,

  ...inputProps
}) => {
  const [field, meta] = useField(name);
  className = getFormFieldClasses({ className, disabled, meta });

  const fieldWrapperProps = {
    ref,
    name,
    label,
    disabled,
    infoText,
    errorMeta: meta,
    fieldClassName,
    showErrorSpace,
  };

  if (type === 'textarea') {
    return (
      <FieldWrapper {...fieldWrapperProps}>
        <Textarea
          {...field}
          id={name}
          disabled={disabled}
          className={className}
          placeholder={placeholder}
        />
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper {...fieldWrapperProps}>
      <Input
        {...field}
        {...inputProps}
        id={name}
        type={type}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        error={meta.touched && meta.error ? meta.error : undefined}
        IconPrefix={IconPrefix}
        IconSuffix={IconSuffix}
        onIconClick={onIconClick}
      />
    </FieldWrapper>
  );
};
